import { useEffect } from 'react';
import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { Nullable } from '@tcc/common';
import { ArdFieldRegionReport, YieldRegionReport } from '../../../../types';
import {
  ArdAsyncStatus,
  ArdFetchResponse,
  fetchArdRegionReport,
} from './services/ardRegionReportQueryService';
import {
  fetchRegionReport,
  getGraphQlErrorMessage,
  validateRegionAgainstField,
} from './services/regionReportQueryService';
import {
  LegacyQueryParams,
  UseReportQueryParams,
  InternalArdQueryParams,
  ArdRequestLifecycle,
  MaybeQueryParams,
  isLegacyQueryParams,
  isArdFullQueryParams
} from './types/queryParams';
import { UseRegionReportQueriesResult } from './types/queryResults';
import { stableSha256FromValue } from '../../../../utils/hash';
import { reportArdRegionReportRequestStarted } from './services/ardRegionReportTelemetry';

  interface UseRegionValidationQueryParams{
  fieldBoundary: unknown;
  isValidationEnabled: boolean;
  queryParams: MaybeQueryParams;
}

const mergeArdRequestLifecycle = (
  currentLifecycle: NonNullable<UseReportQueryParams['ardRequestLifecycleRef']>['current'],
  response: ArdFetchResponse,
) => ({
  cached: currentLifecycle.cached,
  resumed: currentLifecycle.resumed,
  wasCanceled: currentLifecycle.wasCanceled,
  requestId: response.requestId ?? currentLifecycle.requestId,
  pollAttemptCount: response.pollAttemptCount ?? currentLifecycle.pollAttemptCount,
  submitStartedAt: response.submitStartedAt ?? currentLifecycle.submitStartedAt,
  submitCompletedAt: response.submitCompletedAt ?? currentLifecycle.submitCompletedAt,
  pollingStartedAt: response.pollingStartedAt ?? currentLifecycle.pollingStartedAt,
  pollingCompletedAt: response.pollingCompletedAt ?? currentLifecycle.pollingCompletedAt,
  reportFetchStartedAt: response.reportFetchStartedAt ?? currentLifecycle.reportFetchStartedAt,
  reportFetchCompletedAt: response.reportFetchCompletedAt ?? currentLifecycle.reportFetchCompletedAt,
});

interface ArdReportQueryData {
  lifecycle: NonNullable<UseReportQueryParams['ardRequestLifecycleRef']>['current'];
  report: Nullable<ArdFieldRegionReport>;
}

const ARD_LIFECYCLE_QUERY_KEY_PREFIX = 'frr-region-report-lifecycle';

const getQueryKey = (params: MaybeQueryParams, prefix?: string) => {
  const requestReferenceId = [
    ...(prefix ? [prefix] : []),
    params.fieldUUID,
    params.geometryHash,
    ...(isLegacyQueryParams(params) ? [params.seasonTag] : [
      params.seasonYear.toString(),
      params.cropId,
      params.seasonName
    ]),
  ];

  return requestReferenceId;
}

const useRegionValidationQuery = ({
  fieldBoundary,
  isValidationEnabled,
  queryParams,
}: UseRegionValidationQueryParams) => useQuery({
    queryKey: getQueryKey(queryParams, 'frr-region-validity'),
    queryFn: () => validateRegionAgainstField(fieldBoundary, queryParams.geometry),
    enabled: isValidationEnabled,
    retry: false,
    staleTime: 0,
  });

const legacyFetchRegionReportQueryFn = ({
  lastReportGraphQlErrorRef,
  queryParams
}: UseReportQueryParams<LegacyQueryParams>) => () => fetchRegionReport({
  ...queryParams,
  onResponse: (response) => {
    lastReportGraphQlErrorRef.current = getGraphQlErrorMessage(response);
  },
}); 

const ardFetchRegionReportQueryFn = ({
  activityExists,
  lifecycleQueryKey,
  queryClient,
  ardRequestLifecycleRef,
  lastReportGraphQlErrorRef,
  queryParams,
}: UseReportQueryParams<InternalArdQueryParams> & {
  activityExists: boolean;
  lifecycleQueryKey: QueryKey;
  queryClient: ReturnType<typeof useQueryClient>;
}) => async (
  { signal }: { signal?: AbortSignal } = {},
): Promise<ArdReportQueryData> => {
  let lifecycle = ardRequestLifecycleRef?.current ?? { pollAttemptCount: 0 };
  const cachedLifecycle = queryClient.getQueryData<ArdRequestLifecycle>(lifecycleQueryKey);
  if (cachedLifecycle?.requestId) {
    const resumed = cachedLifecycle.wasCanceled ?? false;
    // A cancelled request is resumed, not a completed-report cache hit. Once it
    // completes, persist resumed: false so later opens are cached-only sessions.
    lifecycle = {
      ...lifecycle,
      ...cachedLifecycle,
      cached: !resumed,
      resumed,
      wasCanceled: false,
      pollAttemptCount: 0,
      pollingStartedAt: resumed ? performance.now() : cachedLifecycle.pollingStartedAt,
    };

    if (ardRequestLifecycleRef) {
      ardRequestLifecycleRef.current = lifecycle;
    }
    queryClient.setQueryData(lifecycleQueryKey, lifecycle);
  } else {
    lifecycle = { ...lifecycle, cached: false, resumed: false };
    if (ardRequestLifecycleRef) {
      ardRequestLifecycleRef.current = lifecycle;
    }
    reportArdRegionReportRequestStarted({ activityExists, queryParams });
  }

  try {
    const report = await fetchArdRegionReport({
      ...queryParams,
      requestId: cachedLifecycle?.requestId,
      pollAttemptCount: lifecycle.pollAttemptCount,
      submitStartedAt: cachedLifecycle?.submitStartedAt,
      submitCompletedAt: cachedLifecycle?.submitCompletedAt,
      pollingStartedAt: lifecycle.pollingStartedAt,
      signal,
      onResponse: (response: ArdFetchResponse) => {
        lastReportGraphQlErrorRef.current = getGraphQlErrorMessage(response.error);
        lifecycle = mergeArdRequestLifecycle(lifecycle, response);
        if (ardRequestLifecycleRef) {
          ardRequestLifecycleRef.current = lifecycle;
        }
        queryClient.setQueryData(lifecycleQueryKey, response.status === ArdAsyncStatus.Completed
          ? { ...lifecycle, resumed: false, wasCanceled: false }
          : lifecycle);
      },
    });

    return {
      lifecycle,
      report,
    };
  } catch (error: unknown) {
    if ((error as Error).name === 'AbortError' && lifecycle.requestId) {
      lifecycle = { ...lifecycle, wasCanceled: true };
      queryClient.setQueryData(lifecycleQueryKey, lifecycle);
    }
    throw error;
  }
};

const useReportQuery = ({
  activityExists = false,
  reportQueryEnabled,
  ardRequestLifecycleRef,
  lastReportGraphQlErrorRef,
  queryParams,
}: UseReportQueryParams) => {
  const queryClient = useQueryClient();
  let queryFn;
  const isLegacyQuery = isLegacyQueryParams(queryParams);

  const queryKey = getQueryKey(queryParams, 'frr-region-report');

  if(isLegacyQuery) {
    queryFn = legacyFetchRegionReportQueryFn({
      queryParams,
      reportQueryEnabled,
      lastReportGraphQlErrorRef,
    });
  } else if(isArdFullQueryParams(queryParams)) {
    const lifecycleQueryKey = getQueryKey(queryParams, ARD_LIFECYCLE_QUERY_KEY_PREFIX);
    const reportReferenceId = stableSha256FromValue(queryKey.join('_'));
    queryFn = ardFetchRegionReportQueryFn({
      activityExists,
      lifecycleQueryKey,
      queryClient,
      queryParams: {
        ...queryParams,
        reportReferenceId
      },
      reportQueryEnabled,
      ardRequestLifecycleRef,
      lastReportGraphQlErrorRef,
    });
  }

  return useQuery({
    queryKey,
    queryFn,
    enabled: reportQueryEnabled,
    refetchOnMount: false,
    retry: isLegacyQuery ? 1 : false,
    staleTime: 0,
  });
};
export interface UseRegionReportQueriesParams extends Omit<UseReportQueryParams, 'reportQueryEnabled'> {
  activityExists: boolean;
  fieldBoundary: unknown;
  isValidationEnabled: boolean;
  hasHarvestData: boolean;
}

const isValidSeason = (queryParams: MaybeQueryParams): boolean => {
  if(isLegacyQueryParams(queryParams)) {
    return Boolean(queryParams.seasonTag);
  }

  return Boolean(queryParams.cropId && queryParams.seasonName && queryParams.seasonYear);
}

interface ShouldPerformFrrQueryParams {
  hasHarvestData: boolean;
  isRegionValid: Nullable<boolean>;
  queryParams: MaybeQueryParams;
}

const shouldPerformFrrQuery = ({
  hasHarvestData,
  isRegionValid,
  queryParams
}: ShouldPerformFrrQueryParams) => Boolean(
  hasHarvestData
  && isRegionValid
  && queryParams.fieldUUID
  && isValidSeason(queryParams)
  && queryParams.geometry
);

export const useRegionReportQueries = ({
  activityExists,
  fieldBoundary,
  isValidationEnabled,
  hasHarvestData,
  ardRequestLifecycleRef,
  lastReportGraphQlErrorRef,
  queryParams
}: UseRegionReportQueriesParams): UseRegionReportQueriesResult => {
  const regionValidationQuery = useRegionValidationQuery({
    fieldBoundary,
    isValidationEnabled,
    queryParams
  });

  const isRegionValid = isValidationEnabled
    ? (regionValidationQuery.data ?? null)
    : null;

  const reportQueryEnabled = shouldPerformFrrQuery({
    hasHarvestData,
    isRegionValid,
    queryParams,
  });

  const reportQuery = useReportQuery({
    activityExists,
    reportQueryEnabled,
    ardRequestLifecycleRef,
    lastReportGraphQlErrorRef,
    queryParams
  });

  const ardReportQueryData = isLegacyQueryParams(queryParams)
    ? undefined : reportQuery.data as ArdReportQueryData | undefined

  useEffect(() => {
    if (
      ardRequestLifecycleRef
      && ardReportQueryData?.lifecycle.requestId
      && !ardRequestLifecycleRef.current.submitStartedAt
    ) {
      ardRequestLifecycleRef.current = {
        ...ardReportQueryData.lifecycle,
        cached: true,
        resumed: false,
        wasCanceled: false,
      };
    }
  }, [ardReportQueryData, ardRequestLifecycleRef]);

  const reportData = isLegacyQueryParams(queryParams)
    ? ((reportQuery.data as Nullable<YieldRegionReport | ArdFieldRegionReport>) ?? null)
    : (ardReportQueryData?.report ?? null);
  const reportError = reportQuery.error as Error | undefined;
  const regionValidationError = regionValidationQuery.error as Error | undefined;
  const isValidationLoading = Boolean(
    isValidationEnabled
    && regionValidationQuery.isLoading
    && isRegionValid === null
  );
  const isReportLoading = Boolean(
    reportQueryEnabled
    && (reportQuery.isLoading || (reportQuery.isFetching && !reportData))
  );
  const isLoadingReport = isValidationLoading || isReportLoading;

  return {
    isRegionValid,
    regionValidationError,
    hasRegionValidationError: Boolean(regionValidationError),
    reportData,
    isLoadingReport,
    isReportSuccess: reportQuery.isSuccess,
    isReportFetched: reportQuery.isFetched,
    reportError,
    hasReportError: Boolean(reportError),
    reportDataUpdatedAt: reportQuery.dataUpdatedAt,
  };
};